import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';

import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { Alert } from '@/components/ui/alerts/Alert.comp.tsx';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { type DataOperationsError } from '@/errorHandling/DataOperationsError.util.tsx';
import { isDynamicImportLoadError } from '@/errorHandling/dynamicImportError.util.ts';
import { buildErrorStackTrace } from '@/errorHandling/errorStackTrace.util.ts';
import { captureAppException } from '@/monitoring/sentry.util.ts';
import { reloadPage } from '@/utils/reloadPage.util.ts';

import { DataOperationsErrorContext } from './DataOperationsErrorContext';
import { type ErrorReportStatus, FallbackUI } from './fallbackUI/FallbackUI.comp.tsx';

type Props = {
  children: ReactNode;
  onError?: (error: unknown, errorInfo: ErrorInfo) => Promise<void> | void;
};
type State = {
  context: {
    handleDataOperationError: (error: DataOperationsError) => void;
    handleMinorError: (message?: string) => void;
    handleOperationSuccess: (message?: string) => void;
  };
  errorReportStatus: ErrorReportStatus | null;
  isCorrupted: boolean;
  isMinorError: boolean;
  isSuccess: boolean;
  minorErrorMessage: string | null;
  successMessage: string | null;
};

const IGNORED_ERRORS = ['undefined has no properties', 'is undefined'];
type ErrorReporter = (stackTrace: string) => Promise<void>;

const handleError = (
  error: unknown,
  info: ErrorInfo,
  reportError?: ErrorReporter,
): Promise<void> | void => {
  const stackTrace = buildErrorStackTrace(error, info.componentStack);
  const report = reportError ? () => reportError(stackTrace) : undefined;

  if (import.meta.env.VITE_ENVIRONMENT !== 'local' && isDynamicImportLoadError(error)) {
    if (report) {
      return report().finally(() => reloadPage());
    }
    reloadPage();
    return;
  }

  captureAppException(error, {
    componentStack: info.componentStack ?? undefined,
    contextName: 'react-error-boundary',
  });

  console.debug('ErrorBoundary', error, info);

  return report?.();
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      context: {
        handleDataOperationError: this.handleDataOperationError,
        handleMinorError: this.handleMinorError,
        handleOperationSuccess: this.handleOperationSuccess,
      },
      errorReportStatus: null,
      isCorrupted: false,
      isMinorError: false,
      isSuccess: false,
      minorErrorMessage: null,
      successMessage: null,
    };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    const reportPromise = this.props.onError?.(error, errorInfo);

    this.setState({
      errorReportStatus: reportPromise ? 'pending' : 'failed',
      isCorrupted: true,
    });

    if (reportPromise) {
      void reportPromise.then(
        () => this.setState({ errorReportStatus: 'sent' }),
        () => this.setState({ errorReportStatus: 'failed' }),
      );
    }
  }

  handleDataOperationError = (_error: DataOperationsError) => {
    this.setState({
      errorReportStatus: 'failed',
      isCorrupted: true,
    });
  };

  handleMinorError = (message?: string) => {
    if (!message) {
      this.setState({
        isMinorError: true,
        minorErrorMessage: null,
      });
      return;
    }
    if (IGNORED_ERRORS.some(error => message.toLowerCase().includes(error))) {
      return;
    }
    this.setState({
      isMinorError: true,
      minorErrorMessage: message,
    });
  };

  handleOperationSuccess = (message?: string) => {
    this.setState({
      isSuccess: true,
      successMessage: message ?? null,
    });
  };

  render() {
    const {
      errorReportStatus,
      isCorrupted,
      isMinorError,
      isSuccess,
      minorErrorMessage,
      successMessage,
    } = this.state;
    if (isCorrupted) {
      return <FallbackUI errorReportStatus={errorReportStatus ?? 'failed'} />;
    }
    return (
      <DataOperationsErrorContext value={this.state.context}>
        {this.props.children}
        {isMinorError && (
          <Alert
            handleAlertShown={this.resetMinorErrorState}
            message={minorErrorMessage}
            type="error"
          />
        )}
        {isSuccess && (
          <Alert
            handleAlertShown={this.resetSuccessState}
            message={successMessage}
            type="success"
          />
        )}
      </DataOperationsErrorContext>
    );
  }

  resetMinorErrorState = () => {
    this.setState({
      isMinorError: false,
      minorErrorMessage: null,
    });
  };

  resetSuccessState = () => {
    this.setState({
      isSuccess: false,
      successMessage: null,
    });
  };
}

export const AppErrorBoundary = ({ children }: PropsWithChildren) => (
  <ErrorBoundary onError={handleError}>{children}</ErrorBoundary>
);

export const UserAwareErrorBoundary = ({ children }: PropsWithChildren) => {
  const { frontendErrorsApi } = useApiClient();
  const { user } = useUserContext();

  const reportError =
    user.userId && user.email
      ? (stackTrace: string) =>
          frontendErrorsApi
            .reportFrontendError({
              email: user.email,
              stackTrace,
              userId: user.userId,
            })
            .then(() => undefined)
      : undefined;

  return (
    <ErrorBoundary onError={(error, info) => handleError(error, info, reportError)}>
      {children}
    </ErrorBoundary>
  );
};
